import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { acceptOffer, rejectOffer } from '../api';
import { toApiError } from '../errors';
import { date, money, tenor, titleCase } from '../format';
import { useJourney } from '../journey';
import { useOffer } from '../useOffer';
import Button from '../ui/Button';
import { AmountBanner, DetailRows, Loading, Notice, SectionTitle } from '../ui/Parts';
import Shell from '../ui/Shell';

/** The offer: the amount financed is fixed; the customer picks a tenor from those they can afford. */
const LoanOffer = () => {
  const { steps, current, session, refresh, close } = useJourney();
  const { offer, error: loadError, setOffer } = useOffer(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (offer && selected === null && offer.options.length > 0) {
      setSelected(offer.selectedTenor ?? offer.options[0].tenor);
    }
  }, [offer, selected]);

  const option = offer?.options.find((o) => o.tenor === selected) ?? offer?.options[0];
  const currency = offer?.currency ?? session.currency;

  const respond = async (action: 'accept' | 'reject') => {
    if (!offer || !option) {
      return;
    }
    setError('');
    setBusy(action);
    try {
      setOffer(action === 'accept' ? await acceptOffer(offer.offerId, option.tenor) : await rejectOffer(offer.offerId));
      refresh();
    } catch (e) {
      setError(toApiError(e).message);
      setBusy(null);
    }
  };

  return (
    <Shell
      steps={steps}
      current={current}
      onClose={close}
      footer={
        offer && (
          <div className="grid grid-cols-2 gap-4">
            <Button variant="danger" onClick={() => respond('reject')} loading={busy === 'reject'} disabled={!!busy}>
              Reject Offer
            </Button>
            <Button onClick={() => respond('accept')} loading={busy === 'accept'} disabled={!!busy || !option}>
              Accept Offer
            </Button>
          </div>
        )
      }
    >
      <SectionTitle title="Your Loan Offer" subtitle="Review your financing details and loan terms before deciding whether to proceed." />
      {(error || loadError) && <Notice>{error || loadError?.message}</Notice>}
      {!offer && !loadError && <Loading label="Preparing your offer…" />}
      {offer && option && (
        <>
          {offer.eligibleAmount !== undefined && <AmountBanner label="Eligible amount" amount={money(offer.eligibleAmount, currency)} />}
          <DetailRows
            title="Purchase Breakdown"
            rows={[
              { label: 'Purchase amount:', value: money(offer.purchaseAmount, currency) },
              { label: 'Your upfront payment:', value: money(offer.equityAmount, currency) },
              { label: 'Financed amount:', value: money(offer.financedAmount, currency) },
            ]}
          />
          <DetailRows
            title="Loan Terms"
            rows={[
              { label: 'Repayment frequency:', value: titleCase(offer.repaymentFrequency) },
              { label: 'Each repayment:', value: money(option.instalmentAmount, currency) },
              { label: 'Interest charge:', value: money(option.totalInterest, currency) },
              ...(option.totalFees > 0 ? [{ label: 'Fees:', value: money(option.totalFees, currency) }] : []),
              { label: 'Total repayable:', value: money(option.totalRepayable, currency) },
              { label: 'First repayment date:', value: date(option.firstRepaymentDate) },
            ]}
          >
            <div className="flex items-center justify-between gap-4 mb-2.5 text-sm">
              <label htmlFor="tenor" className="text-lend-body">
                Loan tenor:
              </label>
              <div className="relative">
                <select
                  id="tenor"
                  value={option.tenor}
                  onChange={(e) => setSelected(Number(e.target.value))}
                  disabled={!!busy || offer.options.length === 1}
                  className="appearance-none rounded border border-lend-line bg-lend-field pl-3 pr-8 py-1.5 font-medium text-right focus:outline-none focus:ring-2 focus:ring-lend-blue/40"
                >
                  {offer.options.map((o) => (
                    <option key={o.tenor} value={o.tenor}>
                      {tenor(o.tenor, offer.tenorUnit)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-lend-muted" aria-hidden />
              </div>
            </div>
          </DetailRows>
          {offer.expiresAt && <p className="mt-2 text-xs text-lend-muted">This offer is valid until {date(offer.expiresAt)}.</p>}
        </>
      )}
    </Shell>
  );
};

export default LoanOffer;
